import dayjs from 'dayjs';

import type { IDayAvailability, ITouringHours } from 'src/services/api/touring-hours/types';
import { formatDateTimeForApi } from 'src/utils/format-date-time';

const toMinutes = (time: string): number => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
};

/**
 * Resolve a building-local date (YYYY-MM-DD) to its open intervals, applying a
 * special-day override over the regular weekly periods. This mirrors what the
 * backend used to compute — it only stores the configuration now.
 */
export const getDayAvailability = (
    touringHours: ITouringHours,
    date: string,
): IDayAvailability => {
    const special = touringHours.special_days.find((day) => day.date === date);
    if (special) {
        const intervals = special.is_closed ?
            [] :
            [...special.windows]
                .sort((a, b) => toMinutes(a.open_time) - toMinutes(b.open_time))
                .map((window) => ({ start: window.open_time, end: window.close_time }));
        return { date, is_closed: intervals.length === 0, intervals };
    }
    // Backend periods use 0=Monday … 6=Sunday; dayjs .day() uses 0=Sunday.
    const weekday = (dayjs(date).day() + 6) % 7;
    const intervals = touringHours.periods
        .filter((period) => period.open_day === weekday)
        // A window crossing midnight is clamped to end-of-day for this day's view.
        .map((period) => ({
            start: period.open_time,
            end: period.close_day === weekday ? period.close_time : '24:00',
        }))
        .sort((a, b) => toMinutes(a.start) - toMinutes(b.start));
    return { date, is_closed: intervals.length === 0, intervals };
};

export const formatAvailabilitySummary = (
    availability?: IDayAvailability,
): string => {
    if (!availability) {
        return 'Touring hours are not configured.';
    }
    if (availability.is_closed || availability.intervals.length === 0) {
        return 'Closed for tours this day.';
    }
    return availability.intervals.map((interval) => `${interval.start}-${interval.end}`).join(', ');
};

export const getBuildingLocalDateTime = (
    calendarWallClockDate: Date,
    userTimezone: string,
    buildingTimezone: string,
) => {
    const userWallTime = dayjs(calendarWallClockDate).format('YYYY-MM-DDTHH:mm:ss');
    return dayjs(formatDateTimeForApi(dayjs(userWallTime), userTimezone)).tz(buildingTimezone);
};

export const isBuildingLocalDateTimeWithinAvailability = (
    buildingLocalDateTime: dayjs.Dayjs,
    availability?: IDayAvailability,
): boolean => {
    if (!availability || availability.is_closed) {
        return false;
    }
    const currentMinutes = buildingLocalDateTime.hour() * 60 + buildingLocalDateTime.minute();
    return availability.intervals.some(
        (interval) => toMinutes(interval.start) <= currentMinutes && currentMinutes < toMinutes(interval.end)
    );
};

export const isCalendarSlotOutsideTouringHours = ({
    slotStart,
    userTimezone,
    touringHours,
}: {
    slotStart: Date;
    userTimezone: string;
    touringHours?: ITouringHours | null;
}): boolean => {
    if (!touringHours?.is_set || !touringHours.timezone) {
        return false;
    }
    const buildingLocalDateTime = getBuildingLocalDateTime(
        slotStart,
        userTimezone,
        touringHours.timezone,
    );
    const availability = getDayAvailability(
        touringHours,
        buildingLocalDateTime.format('YYYY-MM-DD'),
    );
    return !isBuildingLocalDateTimeWithinAvailability(buildingLocalDateTime, availability);
};
